import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { User } from '@core/models';
import { AccountService } from '@core/services';
import { Store } from '@ngxs/store';
import { DirectivesModule } from '@shared/directives/directives.module';
import { RelatedToType } from '@shared/enums';
import { UserState } from '@state/user/state';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { Task } from '@tasks/tasks.types';

@Component({
  selector: 'app-task-details-assigned-to',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DirectivesModule,
    MatLegacySlideToggleModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyInputModule,
  ],
  template: ` <div>
    @if (allowAssignToATaskRole) {
      <mat-slide-toggle
        color="primary"
        [checked]="useRole"
        (change)="setUseRole($event)"
        matTooltip="Toggle all"
        [disabled]="!canEditTask"
        mat-flat-ts-button
        [appAuthorize]="canEditTask"
        denyAction="disable"
        [attr.data-testid]="'task-details-task-role-toggle'"
      >
        Assign Task to a Role
      </mat-slide-toggle>
    }

    <form [formGroup]="assignedToForm">
      @if (useRole && allowAssignToATaskRole) {
        <mat-form-field class="w-full field">
          <mat-label>Assigned To Role</mat-label>
          <mat-select
            formControlName="assigned_task_role_id"
            [disabled]="!canEditTask"
            [appAuthorize]="canEditTask"
            denyAction="disable"
            [attr.data-testid]="'task-details-assigned-role-select'"
          >
            <mat-option [value]="null"></mat-option>
            <mat-option *ngFor="let taskRole of taskRoles" [value]="taskRole.id">
              {{ taskRole.name }}
            </mat-option>
          </mat-select>
          <mat-hint *ngIf="assignedToForm.get('assigned_task_role_id').value" class="roles-user-hint"
            >User: {{ task?.assigned_user_name || 'Not Assigned' }}
          </mat-hint>
        </mat-form-field>
      } @else {
        <mat-form-field class="w-full field">
          <mat-label>Assigned To User</mat-label>
          <mat-select
            formControlName="assigned_user_id"
            [disabled]="!canEditTask"
            [appAuthorize]="canEditTask"
            denyAction="disable"
            [attr.data-testid]="'task-details-assigned-user-select'"
          >
            <mat-option [value]="null"></mat-option>
            <mat-option
              *ngFor="let user of users"
              [value]="user?.id"
              [disabled]="!canEditTask || !user?.active"
              [appAuthorize]="canEditTask"
              denyAction="disable"
            >
              {{ user?.full_name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      }
    </form>
  </div>`,
  styleUrl: './task-details-assigned-to.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsAssignedToComponent implements OnInit {
  private _task: Task;
  @Input() canEditTask: boolean;
  @Input() set task(value: Task) {
    this._task = value;
    if (this.assignedToForm) {
      this.assignedToForm.patchValue(
        {
          assigned_user_id: value?.assigned_user_id,
          assigned_task_role_id: value?.assigned_task_role_id,
        },
        { emitEvent: false },
      );
      this.useRole = !!value?.assigned_task_role_id;
    }
  }

  get task(): Task {
    return this._task;
  }
  @Input() users: User[];
  @Input() allowAssignToATaskRole: boolean = false;

  @Output() assignedToChange: EventEmitter<{ assigned_user_id?: number; assigned_task_role_id?: number }> =
    new EventEmitter();

  destroyRef = inject(DestroyRef);
  formBuilder = inject(FormBuilder);
  accountService = inject(AccountService);
  tasksService = inject(TasksService);
  store = inject(Store);
  cd = inject(ChangeDetectorRef);
  prevAssignedFormValue;
  assignedToForm: FormGroup;
  taskRoles;
  useRole = false;

  ngOnInit(): void {
    this.useRole = !!this.task.assigned_task_role_id;

    this.assignedToForm = this.formBuilder.group({
      assigned_user_id: new FormControl(this.task?.assigned_user_id),
      assigned_task_role_id: new FormControl(this.task?.assigned_task_role_id),
    });
    this.prevAssignedFormValue = { ...this.assignedToForm.value };

    this.assignedToForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (this.prevAssignedFormValue.assigned_task_role_id !== value.assigned_task_role_id) {
        value.assigned_user_id = null;
      }

      this.assignedToChange.emit(value);
      this.prevAssignedFormValue = { ...value };
    });

    if (this.allowAssignToATaskRole) {
      this.loadTaskRoles();
    }
  }

  loadTaskRoles(): void {
    const view = this.store.selectSnapshot(TaskState.getTaskView);
    const VALID_APPLICABLE_TYPES = [RelatedToType.Project, RelatedToType.Property];
    let relatedToType;

    if (view.applicable_type) {
      relatedToType = view.applicable_type;
    } else {
      relatedToType = this.task.project
        ? RelatedToType.Project
        : this.task.property
          ? RelatedToType.Property
          : RelatedToType.User;
    }

    if (!VALID_APPLICABLE_TYPES.includes(relatedToType)) {
      this.allowAssignToATaskRole = false;
      return;
    }

    if (!this.taskRoles) {
      if (relatedToType)
        this.tasksService
          .getTaskRolesByType(relatedToType)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((taskRoles) => {
            this.taskRoles = taskRoles;
            this.cd.detectChanges();
          });
    }
  }

  setUseRole(event: MatSlideToggleChange) {
    this.useRole = event.checked;

    if (!this.useRole && this.assignedToForm.get('assigned_task_role_id').value) {
      this.assignedToForm.patchValue({
        assigned_task_role_id: null,
        assigned_user_id: null,
      });
    }
  }
}
